import { ChangeDetectionStrategy, Component, computed, ElementRef, input, model, viewChildren } from '@angular/core';
import { TooltipDirective } from '../tooltip/tooltip.directive';

export interface SegmentedOption {
  value: string;
  label: string;
  /** Removes this one option from selection while leaving the rest of the control usable. */
  disabled?: boolean;
  /** Shown as a tooltip and exposed to screen readers when `disabled` is set. */
  disabledReason?: string;
}

/**
 * A single choice between a few options, shown as one joined control.
 *
 * Follows the standard ARIA radio-group keyboard pattern: only one option is
 * ever a Tab stop (the selected one, or the first selectable one when nothing
 * is selected), and the arrow keys both move focus and select in one step —
 * there is no separate "activate" step, matching a native radio group.
 * Individual options can be disabled (e.g. a step not yet reachable) without
 * disabling the whole control; arrow-key navigation skips over them.
 */
@Component({
  selector: 'app-segmented-control',
  templateUrl: './segmented-control.component.html',
  styleUrl: './segmented-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TooltipDirective],
})
export class SegmentedControlComponent {
  private static nextId = 0;

  readonly options = input.required<readonly SegmentedOption[]>();
  readonly value = model<string | null>(null);
  readonly ariaLabel = input<string>('');
  readonly disabled = input(false);

  private readonly optionButtons = viewChildren<ElementRef<HTMLButtonElement>>('optionButton');

  /** Unique per instance so disabled-reason ids never collide across multiple controls on one page. */
  private readonly instanceId = `sc-${SegmentedControlComponent.nextId++}`;

  /**
   * The one option in the roving-tabindex Tab stop: the selected option (if
   * selectable), the first selectable option otherwise, or the first option
   * of all when every one of them is disabled (so the control never loses
   * its Tab stop entirely).
   */
  readonly tabStopValue = computed<string | null>(() => {
    const opts = this.options();
    if (!opts.length) return null;
    const current = this.value();
    if (current !== null) {
      const currentOption = opts.find((o) => o.value === current);
      if (currentOption && !currentOption.disabled) return current;
    }
    return (opts.find((o) => !o.disabled) ?? opts[0]).value;
  });

  isOptionDisabled(option: SegmentedOption): boolean {
    return this.disabled() || !!option.disabled;
  }

  tabIndexFor(option: SegmentedOption): number {
    return option.value === this.tabStopValue() ? 0 : -1;
  }

  reasonId(option: SegmentedOption, index: number): string | null {
    return option.disabledReason ? `${this.instanceId}-reason-${index}` : null;
  }

  select(value: string): void {
    if (this.disabled()) return;
    const option = this.options().find((o) => o.value === value);
    if (option?.disabled) return;
    this.value.set(value);
  }

  /** Arrow keys move focus and select in the same step, skipping disabled options; Home/End jump to the ends. Wraps at either edge. */
  onKeydown(event: KeyboardEvent, index: number): void {
    if (this.disabled()) return;
    const opts = this.options();
    if (!opts.length || opts.every((o) => o.disabled)) return;

    let step: number;
    let nextIndex: number;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        step = 1;
        nextIndex = (index + step) % opts.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        step = -1;
        nextIndex = (index - 1 + opts.length) % opts.length;
        break;
      case 'Home':
        step = 1;
        nextIndex = 0;
        break;
      case 'End':
        step = -1;
        nextIndex = opts.length - 1;
        break;
      default:
        return;
    }

    // Skip over disabled options in the direction of travel; `every` above
    // guarantees at least one enabled option exists, so this always terminates.
    while (opts[nextIndex].disabled) {
      nextIndex = (nextIndex + step + opts.length) % opts.length;
    }

    event.preventDefault();
    this.select(opts[nextIndex].value);
    this.optionButtons()[nextIndex]?.nativeElement.focus();
  }
}
